import { FocusMonitor } from '@angular/cdk/a11y';
import { BooleanInput, coerceBooleanProperty } from '@angular/cdk/coercion';
import { ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Optional, Output, Self, ViewChild } from '@angular/core';
import { AbstractControl, ControlValueAccessor, FormBuilder, FormControl, FormGroup, NgControl } from '@angular/forms';
import { MatFormFieldControl } from '@angular/material/form-field';
import { Subject } from 'rxjs';
import { faClock } from '@fortawesome/free-solid-svg-icons';
import { TranslateService } from '@ngx-translate/core';

export class TimeRangeControl {
  constructor(public from: string, public to: string) { }
}

@Component({
  selector: 'time-range-input',
  templateUrl: './time-range-input.component.html',
  styleUrls: ['./time-range-input.component.scss'],
  providers: [{ provide: MatFormFieldControl, useExisting: TimeRangeComponent }],
  host: {
    '[class.floating]': 'shouldLabelFloat',
    '[id]': 'id',
  }
})
export class TimeRangeComponent implements MatFormFieldControl<TimeRangeControl>, ControlValueAccessor, OnInit, OnDestroy {
  @Output() timeRangeChange: EventEmitter<any> = new EventEmitter();

  @ViewChild('timeFrom') fromInput: HTMLInputElement;
  @ViewChild('timeTo') toInput: HTMLInputElement;

  static nextId = 0;
  format12 = false;

  parts: FormGroup = this._formBuilder.group({
    from: new FormControl(''),
    to: new FormControl(''),
  });

  faClock = faClock;

  stateChanges = new Subject<void>();
  focused = false;
  touched = false;
  controlType = 'time-range-input';
  id = `time-range-input-${TimeRangeComponent.nextId++}`;
  onChange = (_: any) => { };
  onTouched = () => { };

  get empty() {
    let n = this.parts.value;
    return !n.from && !n.to;
  }

  get shouldLabelFloat() {
    return !this.empty || this.focused;
  }

  @Input()
  get value(): TimeRangeControl | null {
    let n = this.parts.value;
    return new TimeRangeControl(n.from, n.to);
  }
  set value(range: TimeRangeControl | null) {
    range = range || new TimeRangeControl('', '');
    this.parts.setValue({ from: range.from, to: range.to });
    this.stateChanges.next();
  }

  get errorState(): boolean {
    let from = this.parts.value.from;
    let to = this.parts.value.to;

    return (this.parts.invalid || (from && to && from.isAfter(to))) && (this.focused || this.touched);
  }

  @Input()
  get required(): boolean {
    return this._required;
  }
  set required(value: BooleanInput) {
    this._required = coerceBooleanProperty(value);
    this.stateChanges.next();
  }
  private _required = false;

  @Input()
  get disabled(): boolean {
    return this._disabled;
  }
  set disabled(value: BooleanInput) {
    this._disabled = coerceBooleanProperty(value);
    this._disabled ? this.parts.disable() : this.parts.enable();
    this.stateChanges.next();
  }
  private _disabled = false;

  @Input()
  get placeholder() {
    return this._placeholder;
  }
  set placeholder(plh) {
    this._placeholder = plh;
    this.stateChanges.next();
  }
  private _placeholder: string;

  constructor(private _elementRef: ElementRef<HTMLElement>,
    private _formBuilder: FormBuilder,
    private _focusMonitor: FocusMonitor,
    private _translateService: TranslateService,
    @Optional() @Self() public ngControl: NgControl,) {
    if (this.ngControl != null) {
      this.ngControl.valueAccessor = this;
    }

    this.setTimeFormat();

    this._translateService.onLangChange.subscribe(() => {
      this.setTimeFormat();
    });

  }

  ngOnInit() {
  }

  ngOnDestroy() {
    this.stateChanges.complete();
    this._focusMonitor.stopMonitoring(this._elementRef);
  }

  onFocusIn(event: FocusEvent) {
    let target = event.target as HTMLElement;
    if (target.tagName == 'BUTTON') {
      this.onTouched();
      this.focused = false;
      this.stateChanges.next();
      return;
    }
    this.focused = true;
    this.stateChanges.next();
  }

  onFocusOut(event: FocusEvent) {
    if (!this._elementRef.nativeElement.contains(event.relatedTarget as Element)) {
      this.touched = true;
      this.focused = false;
      this.onTouched();
      this.stateChanges.next();
    }
  }

  setTimeFormat() {
    this.format12 = this._translateService.currentLang == 'en';
    this.parts.setValue(this.parts.value);
  }

  setDescribedByIds(ids: string[]) {
    const controlElement = this._elementRef.nativeElement.querySelector(
      '.time-range-input-container',
    )!;
    controlElement.setAttribute('aria-describedby', ids.join(' '));
  }

  onContainerClick() { }

  writeValue(range: TimeRangeControl | null): void {
    this.value = range;
  }

  registerOnChange(fn: any): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: any): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  onValueChange(): void {
    this.previousValueTo = this.value.to;
    this.onChange(this.value);
    this.timeRangeChange.emit(this.value);
  }

  onDateChange(): void {
    this.previousValueTo = this.value.to;
    this.onChange(this.value);
    this.timeRangeChange.emit(this.value);
  }

  previousValueTo = '';
  autoFocusFrom(control: AbstractControl): void {
    const currentValue = control.value;
    if (this.previousValueTo === null || (this.previousValueTo === '' && currentValue.length === 0)) {
      this._focusMonitor.focusVia(this.fromInput, 'program');
    }
    this.previousValueTo = currentValue;
  }
}